import { useState } from 'react';
import { apiGet, apiPost, ApiError } from '../lib/apiClient';
import { useApiResource } from '../lib/useApiResource';
import { useModal } from '../contexts/ModalContext';

/**
 * The revision list — the thing the whole legal sub-project is actually about.
 *
 * Who changed the Terms, when, why, and for a system revision what plan change
 * caused it. Without this the documents are versioned data nobody can audit,
 * which is the state this replaced.
 *
 * A diff loads on demand rather than up front: the list is the common case and
 * a diff is a deliberate act, so fetching every pair eagerly would be a lot of
 * work for something usually not looked at.
 */

interface RevisionSummary {
  id: string;
  revision: number;
  author: { kind: 'admin' | 'system'; displayName: string };
  reason: string;
  trigger?: { kind: string; tier: string };
  createdAt: string;
  publishedAt: string | null;
  isCurrent: boolean;
}

interface DiffChange {
  kind: 'added' | 'removed' | 'changed' | 'unchanged';
  title: string;
  slot?: string;
  before?: string;
  after?: string;
}

const TRIGGER_LABEL: Record<string, string> = {
  plan_price_changed: 'price change',
  plan_features_changed: 'feature change',
};

export function LegalRevisionHistory({
  type,
  label,
  onChanged,
}: {
  type: string;
  label: string;
  onChanged?: () => void;
}) {
  const modal = useModal();
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<number | null>(null);
  const { data, loading, error, reload } = useApiResource<{ revisions: RevisionSummary[] }>(
    () => apiGet<{ revisions: RevisionSummary[] }>(`/admin/legal/${type}/revisions`),
    [type],
  );

  const [diff, setDiff] = useState<{ from: number; to: number; changes: DiffChange[] } | null>(null);
  const [diffError, setDiffError] = useState<string | null>(null);
  const [diffBusy, setDiffBusy] = useState(false);

  const revisions = data?.revisions ?? [];
  const published = revisions.filter((r) => r.publishedAt);

  /**
   * Restore opens a DRAFT carrying that revision's wording. It never rewinds
   * the pointer — a published revision people consented to cannot be
   * un-published — and it deliberately does NOT bring back the generated
   * section, because that would republish an old PRICE. The dialog says both,
   * because "restore" sounds like it undoes more than it does.
   */
  const restore = async (revision: number) => {
    const confirmed = await modal.confirm({
      title: `Restore the wording of v${revision}?`,
      message:
        `This opens a draft containing v${revision}'s text. It does not un-publish anything — ` +
        `publishing the draft creates a NEW revision.\n\n` +
        'Sections generated from the plan catalog are not restored: they stay as they are today, ' +
        'so an old price cannot come back with the old wording.',
      confirmLabel: `Open a draft from v${revision}`,
    });
    if (!confirmed) return;

    setRestoring(revision);
    setRestoreError(null);
    try {
      await apiPost(`/admin/legal/${type}/restore/${revision}`);
      reload();
      onChanged?.();
    } catch (err) {
      setRestoreError(err instanceof ApiError ? err.message : 'Could not restore that revision');
    } finally {
      setRestoring(null);
    }
  };

  const showDiff = async (to: number) => {
    const from = to - 1;
    if (from < 1) return;
    setDiffBusy(true);
    setDiffError(null);
    try {
      const result = await apiGet<{ from: number; to: number; changes: DiffChange[] }>(
        `/admin/legal/${type}/diff?from=${from}&to=${to}`,
      );
      setDiff(result);
    } catch {
      setDiffError(`Could not load the diff between v${from} and v${to}.`);
    } finally {
      setDiffBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">{label} — revision history</h3>
      </div>
      <div className="card-body">
        {loading && !data && <p className="cell-sub">Loading…</p>}
        {error && <div className="error-banner">{error}</div>}

        {revisions.length > 0 && (
          <div className="table-wrap">
            <table className="data-table dt-table">
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Author</th>
                  <th>Reason</th>
                  <th>Published</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {revisions.map((r) => (
                  <tr key={r.id}>
                    <td>
                      {r.publishedAt ? `v${r.revision}` : 'Draft'}
                      {r.isCurrent && <span className="cell-sub"> · live</span>}
                    </td>
                    <td>
                      {r.author.displayName}
                      {r.trigger && (
                        <span className="cell-sub">
                          {' '}
                          · {TRIGGER_LABEL[r.trigger.kind] ?? r.trigger.kind} on {r.trigger.tier}
                        </span>
                      )}
                    </td>
                    <td className="cell-sub">{r.reason}</td>
                    <td className="cell-sub">
                      {r.publishedAt ? new Date(r.publishedAt).toLocaleDateString() : '—'}
                    </td>
                    <td>
                      {r.publishedAt && r.revision > 1 && (
                        <button
                          type="button"
                          className="btn"
                          disabled={diffBusy}
                          onClick={() => showDiff(r.revision)}
                        >
                          Diff vs v{r.revision - 1}
                        </button>
                      )}
                      {r.publishedAt && !r.isCurrent && (
                        <button
                          type="button"
                          className="btn"
                          disabled={restoring !== null}
                          onClick={() => restore(r.revision)}
                        >
                          {restoring === r.revision ? 'Restoring…' : 'Restore wording'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {published.length === 0 && !loading && (
          <p className="cell-sub">No revisions published yet.</p>
        )}

        {diffError && <div className="error-banner">{diffError}</div>}
        {restoreError && (
          <div role="alert" className="error-banner">
            {restoreError}
          </div>
        )}

        {diff && (
          <div className="card" style={{ marginTop: '1rem' }}>
            <div className="card-header">
              <h3 className="card-title">
                v{diff.from} → v{diff.to}
              </h3>
            </div>
            <div className="card-body">
              {/* Unchanged sections are counted, not listed. The point of the
                  diff is that a plan change touches ONE section and leaves the
                  authored prose alone — a wall of identical text would bury it. */}
              <p className="cell-sub" data-testid="diff-summary">
                {`${diff.changes.filter((c) => c.kind !== 'unchanged').length} section(s) changed · ${diff.changes.filter((c) => c.kind === 'unchanged').length} unchanged`}
              </p>
              {diff.changes
                .filter((c) => c.kind !== 'unchanged')
                .map((c) => (
                  <div key={c.title} style={{ marginBottom: '1rem' }}>
                    <p>
                      <strong>{c.title}</strong>{' '}
                      <span className="cell-sub">
                        {c.kind}
                        {c.slot ? ' · generated from the plan catalog' : ''}
                      </span>
                    </p>
                    {c.before && (
                      <p className="cell-sub">
                        <em>Before:</em> {c.before.slice(0, 400)}
                      </p>
                    )}
                    {c.after && (
                      <p className="cell-sub">
                        <em>After:</em> {c.after.slice(0, 400)}
                      </p>
                    )}
                  </div>
                ))}
              <button type="button" className="btn" onClick={() => setDiff(null)}>
                Close diff
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
